import { LayoutType } from "@kalyzee/escaet-ui";
import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { useMemo } from "react";
import { getFormattedUserFromMeetingSession } from "../../../helpers/meeting";
import { PopulatedMeetingSession } from "../../../interfaces/populatedData";
import { ScreenMediaName } from "../../media/ScreenShare";
import { PlayerItem, PlayerRendererProvider } from "../interfaces";
import { useFocusedMeetingAPI, useMeetingSelector } from "../../../hooks/meeting";
import MeetingSessionMediaPlayer from "../MeetingSessionMediaPlayer";
import { MeetingPlayerHighlightContainer } from "./MeetingPlayerHighlightContainer";
import { MeetingPlayerTriLayoutContainer } from "./MeetingPlayerTriLayoutContainer";
import { usePopulatedMeetingData } from "../../../hooks/populatedData";
import { MeetingRoomStatus, MeetingRoomType } from "@kalyzee/kast-web-sdk/websocket";
import { expertWebcamName, pedagogyWebcamName } from "../../../helpers/webcam";
import { FullScreenRenderer } from "../../renderers/FullScreenRenderer";
import { FullScreenButton } from "../../utils/FullScreenButton";

export interface RemotesToExpertsContainerProps {
  expertSessions?: PopulatedMeetingSession[];
  remoteSessions?: PopulatedMeetingSession[];
  self?: {
    present: boolean;
    videoStream?: MediaStream;
    screens?: boolean;
  };
  layout?: LayoutType;
  showRemoteGroup?: boolean;
}

export const RemotesToExpertsContainer = ({
  expertSessions = [],
  remoteSessions = [],
  self,
  layout = LayoutType.Square,
  showRemoteGroup,
}: RemotesToExpertsContainerProps) => {
  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  const meetingCtx = useFocusedMeetingAPI();
  const [meeting] = useMeetingSelector(meetingCtx, (state) => state.meeting);
  const [session] = useMeetingSelector(meetingCtx, (state) => state.session);
  const [emisionSessions] = useMeetingSelector(meetingCtx, (state) => state.emissionSessions);
  const populatedMeetingData = usePopulatedMeetingData(meetingCtx);

  const expertWebcamVideoSessions = useMemo(
    () => expertSessions?.flatMap((session) => session.videoSessions).filter((session) => {
      const name = session.data?.mediaSession.name;
      if (!name) return false;
      return [expertWebcamName, pedagogyWebcamName, ScreenMediaName].includes(name);
    }),
    [expertSessions]
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return null;

  const renderExpertPlayer = (session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>) => {
    const meetingSession = session.data?.session;
    const isPeda = meetingSession.extra?.isPedagogy;
    const isExpert = !isPeda;
    return (
      <MeetingSessionMediaPlayer
        key={`meeting-session-player-${session.data.session.id}`}
        style={{ width: "100%", height: "100%" }}
        meeting={meeting}
        sessionId={session.data.session.id}
        mediaSessionId={session.data.mediaSession?.id!}
        ovenMediaSession={session}
        muted
        hideSoundEffect
        autoOnOffVideo
        renderCustomContainer={(children, context) => {
          const names = getFormattedUserFromMeetingSession(meetingSession, meeting);
          return (
            <FullScreenRenderer
              render={(enabled, setEnabled) => (
                <MeetingPlayerHighlightContainer
                  fullName={names.fullname}
                  isPlayingSound={context?.isPlayingSound}
                  isExpert={isExpert}
                  isPeda={isPeda}
                  soundEffect
                  isMainMode={enabled}
                  isScreenShare={session.data.mediaSession.name === ScreenMediaName}
                  style={{ width: "100%", height: "unset", flex: "0 1 auto", minHeight: 0 }}
                  extraButtons={[
                    <FullScreenButton enabled={enabled} setEnabled={setEnabled} transparentBg />
                  ]}
                >
                  {children}
                </MeetingPlayerHighlightContainer>
              )} 
            />
          );
        }}
      />
    );
  };

  const renderItem = (rendererProvider: PlayerRendererProvider, item: PlayerItem) => {
    const itemData = item.getData();
    let name = "Vous";
    let isScreenShare = false;
    if (itemData.type === "video_session") {
      const meetingSession = itemData.videoSession.data?.session;
      const names = getFormattedUserFromMeetingSession(meetingSession, meeting);
      name = names.fullname;
      isScreenShare = itemData.videoSession.data.mediaSession.name === ScreenMediaName;
    } else if (itemData.type === "stream") {
      isScreenShare = itemData.isScreenShare ?? isScreenShare;
    }

    return rendererProvider((children, context) => {
      const isLeader = itemData.extra?.isLeaderSession;
      return (
        <FullScreenRenderer
          render={(enabled, setEnabled) => (
            <MeetingPlayerHighlightContainer
              fullName={name}
              groupName={showRemoteGroup ? itemData.extra?.meetingSession?.user.group?.room.name : undefined}
              isPlayingSound={context?.isPlayingSound}
              soundEffect
              style={{ width: "100%", height: "100%" }}
              isMainMode={enabled}
              isScreenShare={isScreenShare}
              isLeader={isLeader}
              extraButtons={[
                <FullScreenButton enabled={enabled} setEnabled={setEnabled} transparentBg />
              ]}
            >
              {children}
            </MeetingPlayerHighlightContainer>
          )}
        />
      );
    });
  };

  const renderRemoteLayout = (items: PlayerItem[], flex = 2, style?: React.CSSProperties) => {
    if (!items.length) return null;
    return (
      <div
        style={{
          flex,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "1em",
          height: "100%",
          minHeight: 0,
          ...(style ?? {}),
        }}
      >
        <MeetingPlayerTriLayoutContainer meeting={meeting} items={items} renderItem={renderItem} layout={layout} />
      </div>
    );
  };

  const renderExpertLayout = (flex = 1, style?: React.CSSProperties) => {
    return (
      <div
        style={{
          flex,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          overflowY: "auto",
          gap: "1rem",
          minHeight: 0,
          ...(style ?? {}),
        }}
      >
        {expertWebcamVideoSessions.map((session) => renderExpertPlayer(session))}
      </div>
    );
  };

  const renderContent = () => {
    const selfPopulatedSession = populatedMeetingData?.me?.sessions?.find((s) => s.id === session?.id);
    const selfRoom = populatedMeetingData?.me?.group?.room;
    let reverseMode = false; // reverseMode only if is a one to one (me and expert)
    const remoteItems: PlayerItem[] = [];
    if (self?.videoStream) {
      reverseMode = true;
      remoteItems.push(
        new PlayerItem({
          type: "stream",
          mediaStream: self?.videoStream,
          extra: {
            isLeaderSession: selfPopulatedSession && selfRoom?.type === MeetingRoomType.Group && selfRoom?.status === MeetingRoomStatus.Ongoing && selfRoom?.leaderUserId === selfPopulatedSession.user?.id,
          },
        })
      );
    }

    if (self?.screens) {
      const medias = session?.medias;
      medias?.forEach((m) => {
        if (m.name !== ScreenMediaName) return;
        const session = emisionSessions?.find((s) => s.data?.mediaSession?.id === m.id);
        if (session?.stream) {
          remoteItems.push(
            new PlayerItem({
              type: "stream",
              mediaStream: session.stream.getMediaStream(),
              isScreenShare: true,
            })
          );
        }
      });
    }

    const otherRemoteItems: PlayerItem[] = [];
    remoteSessions?.forEach((session) => {
      session.videoSessions.forEach((videoSession) => {
        const currentRoom = session?.user?.group?.room;
        otherRemoteItems.push(
          new PlayerItem({
            type: "video_session",
            videoSession,
            extra: {
              meetingSession: session,
              isLeaderSession: session && currentRoom?.type === MeetingRoomType.Group && currentRoom?.status === MeetingRoomStatus.Ongoing && currentRoom?.leaderUserId === session.user?.id,
            },
          })
        );
      });
    });
    if (otherRemoteItems.length) {
      reverseMode = false;
      remoteItems.push(...otherRemoteItems);
    }
    const flexDirection = reverseMode ? "row-reverse" : "row";
    const remoteLayoutFlex = reverseMode ? 1 : 2;
    const expertLayoutFlex = reverseMode ? 3 : 1;
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection,
          gap: "1em",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {renderRemoteLayout(remoteItems, remoteLayoutFlex, reverseMode && expertWebcamVideoSessions.length ? { maxHeight: "50%" } : undefined)}
        {expertWebcamVideoSessions.length ? renderExpertLayout(expertLayoutFlex) : null}
      </div>
    );
  };

  return renderContent();
};
